import { Component, ComponentFactoryResolver, ViewContainerRef, EventEmitter, Renderer2, QueryList, ViewChildren, OnDestroy, Input, Output } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { Utils } from '../../utils';
import { SlideshowService } from '../slideshow.service';
import { SlideComponent } from './slide.component';

@Component({
    selector: 'slides',
    template: `
        <div *ngIf="showPlaceholder" class="loading-placeholder">
            <p>
                <span class="line placeholder"></span>
                <span class="line placeholder"></span>
                <span class="line placeholder"></span>
            </p>
            <div class="title placeholder"></div>
            <div class="image placeholder"></div>
            <p>
                <span class="line placeholder"></span>
                <span class="line placeholder"></span>
                <span class="line placeholder"></span>
            </p>
            <div class="title placeholder"></div>
            <div class="image placeholder"></div>
            <p>
                <span class="line placeholder"></span>
                <span class="line placeholder"></span>
                <span class="line placeholder"></span>
            </p>
        </div>
        <div #container *ngFor="let slide of slideshow.slides; let i = index" class="anchor" id="slide-{{i}}"></div>
    `
})
export class SlidesComponent implements OnDestroy {
    loading = true;
    showPlaceholder = true;
    windowScrollSubscription: Subscription;
    slideContentChangeSubscription: Subscription;
    slideElsChange: Subscription;

    @Input('animationComplete') 
    set animationComplete(animationComplete) {
        if( animationComplete ) {
            this.loadAndMeasure();
        }
    }

    @Output() placeholderVisible = new EventEmitter();

    @ViewChildren('container', {read: ViewContainerRef}) containers: QueryList<any>;

    constructor(
        public slideshow: SlideshowService, 
        private cfr: ComponentFactoryResolver,
        private render: Renderer2,
        public utils: Utils
    ) {}
    ngAfterViewInit() {
        this.placeholderVisible.emit(this.showPlaceholder);
        this.slideElsChange = this.containers.changes.subscribe(list => {
            if( this.loading ) {
                this.loadAndMeasure();
                return;
            }
            this.checkSlides();
        });
        this.windowScrollSubscription = fromEvent(window, 'scroll').subscribe(() => {
            this.checkSlides();
        });
        this.slideContentChangeSubscription = this.slideshow.slideContentChange.subscribe(slide => {
            const index = this.slideshow.slides.indexOf(slide);
            const containers = this.containers.toArray();
            if( containers[index] ) {
                this.setHeight(containers[index]);
            } 
        });
    }
    // progressive rendering modified from https://blog.bitsrc.io/3-ways-to-render-large-lists-in-angular-9f4dcb9b65
    loadInterval;
    loadAndMeasure() {
        clearInterval(this.loadInterval);
        const containers = this.containers.toArray();
        const length = containers.length;
        const ITEMS_RENDERED_AT_ONCE = 5;
        const INTERVAL_IN_MS = 150;

        let currentIndex = 0;

        this.loadInterval = setInterval(() => {
            const nextIndex = currentIndex + ITEMS_RENDERED_AT_ONCE;

            for (let n = currentIndex; n <= nextIndex ; n++) {
                if (n >= length) {
                    this.loading = false;
                    clearInterval(this.loadInterval);
                    break;
                }
                const container = containers[n];
                if( !container || container.component ) {
                    continue;
                }
                this.insertComponent(container, n);
            }

            currentIndex += ITEMS_RENDERED_AT_ONCE;
        }, INTERVAL_IN_MS);
    }
    checking = false;
    checkSlides() {
        if( this.checking ) {
            return;
        }
        this.checking = true;
        const containers = this.containers.toArray();
        const slides = this.slideshow.slides;
        const length = containers.length;
        for(var i = 0; i < length; i++) {
            const container = containers[i];
            if( !this.inView(container.element.nativeElement) ) {
                this.removeComponent(container);
                continue;
            }
            if( container.component ) {
                const slide = slides[i];
                container.component.instance.i = i;
                container.component.instance.slide = slide;
                container.component.instance.caption = slide.images.length > 0 ? slide.images[0].caption : '';
                container.component.instance.cdr.markForCheck();
                if( !container.element.nativeElement.contains(container.component.instance.ref.nativeElement) ) { // angular :\
                    this.render.appendChild(container.element.nativeElement, container.component.instance.ref.nativeElement);
                }
                this.setHeight(container);
                continue;
            }
            this.insertComponent(container, i);
        }
        this.checking = false;
    }
    maybeRemoveComponent(container) {
        if( this.inView(container.element.nativeElement) ) {
            return;
        }
        this.removeComponent(container);
    }
    removeComponent(container) {
        if( !container.component ) {
            return;
        }
        container.component.destroy();
        delete container.component;
    }
    setHeight(container) {
        const height = container.component.instance.ref.nativeElement.offsetHeight;
        if( !height ) {
            return;
        }
        container.element.nativeElement.style.minHeight = height + 'px';
    }
    insertComponent(container, index) {
        container.clear();
        container.element.nativeElement.innerHTML = '';
        const factory = this.cfr.resolveComponentFactory(SlideComponent);
        const componentRef = container.createComponent( factory );
        this.render.appendChild(container.element.nativeElement, componentRef.instance.ref.nativeElement);
        const slide = this.slideshow.slides[index];
        componentRef.instance.slide = slide;
        componentRef.instance.i = index;
        componentRef.instance.caption = slide.images.length > 0 ? slide.images[0].caption : '';
        container.component = componentRef;
        const slideLoadedSubscription = componentRef.instance.viewInit.subscribe(() => {
            if( this.showPlaceholder !== false ) {
                this.showPlaceholder = false;
                this.placeholderVisible.emit(this.showPlaceholder);
            }
            this.setHeight(container);
            this.maybeRemoveComponent(container);
            slideLoadedSubscription.unsubscribe();
        });
    }
    inView(el):boolean {
        let top = el.offsetTop;
        let height = el.offsetHeight;
        let buffer = 1000;

        while(el.offsetParent) {
            el = el.offsetParent;
            top += el.offsetTop;
        }
        return (
            top < (window.pageYOffset + window.innerHeight + buffer) &&
            (top + height + buffer) > window.pageYOffset
        );
    }
    ngOnDestroy() {
        this.loading = true;
        clearInterval(this.loadInterval);
        this.checking = false;
        this.showPlaceholder = true;
        this.placeholderVisible.emit(this.showPlaceholder);
        if( this.windowScrollSubscription ) {
            this.windowScrollSubscription.unsubscribe();
        }
        if( this.slideContentChangeSubscription ) {
            this.slideContentChangeSubscription.unsubscribe();
        }
        if( this.slideElsChange ) {
            this.slideElsChange.unsubscribe();
        }
    }
}